/**
 * ChatPanel - Main AI Financial Advisor chat interface
 */

import * as React from 'react'
import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  closeChat,
  minimizeChat,
  maximizeChat,
  addUserMessage,
  addAssistantMessage,
  setChatLoading,
  setChatError,
  startNewSession,
} from '@/store'
import { useFinancialContext } from '@/hooks/useFinancialContext'
import { sendMessage } from '@/services/aiAdvisor'
import { SUGGESTED_PROMPTS } from '@/types/chat.types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  X,
  Minus,
  Send,
  Loader2,
  Bot,
  User,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Settings,
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'

interface ChatPanelProps {
  apiKey: string
  onSettingsClick?: () => void
}

export function ChatPanel({ apiKey, onSettingsClick }: ChatPanelProps) {
  const dispatch = useAppDispatch()
  const { isOpen, isMinimized, messages, isLoading, error, suggestedPromptsVisible } = 
    useAppSelector((state) => state.chat)
  const context = useFinancialContext()
  
  const [inputValue, setInputValue] = React.useState('')
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  
  // Scroll to bottom when messages change
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])
  
  // Focus input when panel opens
  React.useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, isMinimized])
  
  // Handle send message
  const handleSend = async () => {
    const trimmedInput = inputValue.trim()
    if (!trimmedInput || isLoading) return
    
    if (!apiKey) {
      dispatch(setChatError('Please add your Gemini API key in settings to use the AI advisor.'))
      return
    }
    
    setInputValue('')
    dispatch(addUserMessage(trimmedInput))
    dispatch(setChatLoading(true))
    
    try {
      const response = await sendMessage(trimmedInput, messages, context, apiKey)
      dispatch(addAssistantMessage({ content: response }))
    } catch (err) {
      dispatch(setChatError(err instanceof Error ? err.message : 'Failed to get response'))
    } finally {
      dispatch(setChatLoading(false))
    }
  }
  
  // Handle suggested prompt click
  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt)
    inputRef.current?.focus()
  }
  
  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }
  
  // Filter relevant suggested prompts based on user's situation
  const relevantPrompts = React.useMemo(() => {
    return SUGGESTED_PROMPTS.filter(prompt => {
      if (!prompt.showIf) return true
      
      const { hasChildren, hasDebt, nearRetirement, hasGoals, lowSavingsRate, highDebtRatio } = prompt.showIf
      
      if (hasChildren !== undefined && hasChildren !== (context.family.numberOfChildren > 0)) return false
      if (hasDebt !== undefined && hasDebt !== (context.finances.totalDebts > 0)) return false
      if (nearRetirement !== undefined && nearRetirement !== (context.personal.yearsToRetirement <= 10)) return false
      if (hasGoals !== undefined && hasGoals !== (context.goals.totalGoals > 0)) return false
      if (lowSavingsRate !== undefined && lowSavingsRate !== (context.finances.savingsRate < 10)) return false
      if (highDebtRatio !== undefined && highDebtRatio !== (context.debts.debtToIncomeRatio > 40)) return false
      
      return true
    }).slice(0, 6)
  }, [context])
  
  // Get icon component
  const getIcon = (iconName: string) => {
    return LucideIcons[iconName as keyof typeof LucideIcons] as React.ComponentType<{ className?: string }> | undefined
  }
  
  if (!isOpen) return null
  
  // Minimized state
  if (isMinimized) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <Button
          onClick={() => dispatch(maximizeChat())}
          className="h-14 rounded-full shadow-lg"
        >
          <Bot className="mr-2 h-5 w-5" />
          AI Advisor
          {messages.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {messages.length}
            </Badge>
          )}
        </Button>
      </motion.div>
    )
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-4 right-4 z-50 w-[400px] max-w-[calc(100vw-2rem)]"
    >
      <Card className="flex flex-col h-[600px] max-h-[calc(100vh-2rem)] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground rounded-t-lg">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <span className="font-semibold">AI Financial Advisor</span>
          </div>
          <div className="flex items-center gap-1">
            {onSettingsClick && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={onSettingsClick}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={() => dispatch(minimizeChat())}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={() => dispatch(closeChat())}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Messages */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4"
        >
          <div className="space-y-4">
            {/* Welcome message if no messages */}
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">
                  Hi! I'm your AI Financial Advisor
                </h3>
                <p className="text-muted-foreground text-sm mb-6">
                  I have full context of your financial situation. Ask me anything about
                  retirement, debt, investments, or your life plan.
                </p>
              </div>
            )}
            
            {/* Suggested prompts */}
            {suggestedPromptsVisible && messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase">
                  Suggested Questions
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {relevantPrompts.map((prompt) => {
                    const IconComponent = getIcon(prompt.icon)
                    return (
                      <button
                        key={prompt.id}
                        onClick={() => handlePromptClick(prompt.prompt)}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg text-left text-sm',
                          'bg-muted/50 hover:bg-muted transition-colors'
                        )}
                      >
                        {IconComponent && (
                          <IconComponent className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <span>{prompt.prompt}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
            
            {/* Messages */}
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3',
                  message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                    message.role === 'user' ? 'bg-primary' : 'bg-muted'
                  )}
                >
                  {message.role === 'user' ? (
                    <User className="h-4 w-4 text-primary-foreground" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
                <div
                  className={cn(
                    'rounded-lg px-4 py-2 max-w-[85%]',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.error && (
                    <p className="text-xs text-destructive mt-1">{message.error}</p>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            
            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 flex-shrink-0"
                onClick={() => dispatch(startNewSession())}
                title="Start new conversation"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="flex-1"
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
              className="h-10 w-10 flex-shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            AI responses are for informational purposes only
          </p>
        </div>
      </Card>
    </motion.div>
  )
}
